/**
 * „Nowy wyjątek na dzień" (T-B2w) — one date's hours, or the date closed.
 *
 * The date opens on whatever it says now (its standing exception, or its weekday's hours); moving
 * the date re-opens the draft on the new date's own — unless the trainer already edited the hours,
 * which then stay (`exceptionMovedTo`). The native date field keeps the reader's own format, so the
 * date is also written out in words beside it, and „Zwykle w poniedziałek: …" names the weekday.
 * A date is stated no later than the last day the tab reads (`lastStatableDay`), and a date the
 * dialog cannot take says why under the field, tied to it. The dialog opens on the date field.
 * The closed-day switch makes the windows under it irrelevant and they are not sent. „Zapisz
 * wyjątek" stays focusable while it cannot send and says why; a refusal keeps the dialog open and
 * says why, act first. Base UI's dialog traps focus and returns it to what opened it.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import { ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import { Toggle } from '~/shared/ui/Toggle';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import { fullDate } from '../model/calendarWords';
import {
  exceptionClosedToggled,
  exceptionVerdict,
  exceptionWindowAdded,
  exceptionWindowRemoved,
  exceptionWindowRetimed,
  lastStatableDay,
  MAX_DATE_WINDOWS,
  type ExceptionDraft,
} from '../model/dateException';
import { windowsSpan } from '../model/scheduleWords';
import { isoWeekday, type CivilDay } from '../model/trainerClock';
import type { WorkingWindow } from '../model/workingHours';
import { WindowFields } from './WindowFields';
import styles from './ScheduleTab.module.css';

interface ExceptionDialogProps {
  readonly draft: ExceptionDraft | null;
  /** The weekday's own hours on the draft's date. */
  readonly usual: readonly WorkingWindow[];
  readonly today: CivilDay;
  readonly saving: boolean;
  readonly refusal: string | null;
  readonly onEdit: (draft: ExceptionDraft) => void;
  /** Move the dialog to another date — it opens on what that date says. */
  readonly onMoveDate: (date: CivilDay) => void;
  readonly onSave: () => void;
  readonly onClose: () => void;
}

export function ExceptionDialog({
  draft,
  usual,
  today,
  saving,
  refusal,
  onEdit,
  onMoveDate,
  onSave,
  onClose,
}: ExceptionDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const dateId = useId();
  const dateWordsId = useId();
  const dateErrorId = useId();
  const reasonId = useId();
  const dateField = useRef<HTMLInputElement>(null);
  const lastDay = lastStatableDay(today);
  const verdict = draft === null ? null : exceptionVerdict(draft, today);
  const dateError =
    verdict?.dateProblem === 'past'
      ? t('web:booking.schedule.datePast')
      : verdict?.dateProblem === 'tooFar'
        ? t('web:booking.schedule.dateTooFar', { date: fullDate(lastDay, locale) })
        : null;
  const weekday = draft === null ? null : isoWeekday(draft.date);
  const blocked =
    verdict === null
      ? null
      : dateError !== null
        ? dateError
        : verdict.empty
          ? t('web:booking.trainer.exception.hintWindows')
          : verdict.problem !== null
            ? t('web:booking.schedule.invalid')
            : null;

  return (
    <Dialog.Root
      open={draft !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <Dialog.Popup
          className={`${String(surface.popup)} ${String(form.dialog)}`}
          initialFocus={dateField}
        >
          {draft === null || verdict === null ? null : (
            <>
              <div className={form.dialogHead}>
                <div>
                  <span className={form.eyebrow}>{t('web:booking.trainer.exception.eyebrow')}</span>
                  <Dialog.Title className={form.dialogTitle}>
                    {draft.replacing
                      ? t('web:booking.trainer.exception.editTitle')
                      : t('web:booking.trainer.exception.title')}
                  </Dialog.Title>
                </div>
                <Dialog.Close className={form.close} aria-label={t('web:booking.schedule.close')}>
                  <CloseGlyph className={form.closeGlyph} />
                </Dialog.Close>
              </div>

              <div className={form.field}>
                <label className={form.fieldLabel} htmlFor={dateId}>
                  {t('web:booking.trainer.exception.date')}
                </label>
                <span className={styles.dateRow}>
                  <input
                    ref={dateField}
                    id={dateId}
                    type="date"
                    className={`${String(form.input)} ${dateError === null ? '' : String(form.inputBad)}`}
                    value={draft.date}
                    min={today}
                    max={lastDay}
                    aria-invalid={dateError === null ? undefined : true}
                    aria-describedby={
                      dateError === null ? dateWordsId : `${dateWordsId} ${dateErrorId}`
                    }
                    onChange={(event) => {
                      if (/^\d{4}-\d{2}-\d{2}$/.test(event.target.value))
                        onMoveDate(event.target.value);
                    }}
                  />
                  <span id={dateWordsId} className={styles.dateWords}>
                    {fullDate(draft.date, locale)}
                  </span>
                </span>
              </div>
              {dateError === null ? null : (
                <p id={dateErrorId} className={form.fieldError}>
                  <WarningGlyph className={styles.warnGlyph} />
                  <span>{dateError}</span>
                </p>
              )}
              <p className={styles.usual}>
                {usual.length === 0
                  ? t(`web:booking.schedule.usualFreeOn.${String(weekday)}`)
                  : t(`web:booking.schedule.usualOn.${String(weekday)}`, {
                      windows: windowsSpan(usual),
                    })}
              </p>
              {draft.replacing ? (
                <p className={form.note}>{t('web:booking.trainer.exception.replacing')}</p>
              ) : null}

              <div className={styles.closedRow}>
                <span className={styles.closedText}>
                  <span className={styles.closedTitle}>
                    {t('web:booking.trainer.exception.closed')}
                  </span>
                  <span className={form.note}>{t('web:booking.trainer.exception.closedSub')}</span>
                </span>
                <Toggle
                  checked={draft.closed}
                  label={t('web:booking.trainer.exception.closed')}
                  onChange={() => {
                    onEdit(exceptionClosedToggled(draft));
                  }}
                />
              </div>

              {draft.closed ? null : (
                <div className={form.field} data-window-scope>
                  <span className={form.fieldLabel}>{t('web:booking.schedule.windowsLabel')}</span>
                  <WindowFields
                    wide
                    windows={draft.windows}
                    bad={verdict.bad}
                    problem={verdict.problem}
                    onRetime={(id, change) => {
                      onEdit(exceptionWindowRetimed(draft, id, change));
                    }}
                    onRemove={(id) => {
                      onEdit(exceptionWindowRemoved(draft, id));
                    }}
                  />
                  {verdict.canAdd ? (
                    <button
                      type="button"
                      className={`${String(styles.tinyGhost)} ${String(styles.addWindow)}`}
                      data-add-window
                      onClick={() => {
                        onEdit(exceptionWindowAdded(draft));
                      }}
                    >
                      <ScreenHeaderPlus className={styles.tinyGlyph} />
                      {t('web:booking.trainer.day.addWindow')}
                    </button>
                  ) : verdict.atLimit ? (
                    <p className={form.note}>
                      {t('web:booking.trainer.exception.windowLimit', { max: MAX_DATE_WINDOWS })}
                    </p>
                  ) : null}
                </div>
              )}

              {refusal === null ? null : (
                <p className={form.refusal} role="alert">
                  {refusal}
                </p>
              )}

              <div className={form.dialogActs}>
                <button type="button" className={surface.ghost} onClick={onClose}>
                  {t('web:booking.schedule.cancel')}
                </button>
                {/* A date's refusal is said under the date field; the button points there. */}
                {blocked === null || dateError !== null ? null : (
                  <span id={reasonId} className={form.actReason}>
                    {blocked}
                  </span>
                )}
                <button
                  type="button"
                  className={surface.primary}
                  aria-disabled={blocked !== null || saving ? true : undefined}
                  aria-describedby={
                    dateError !== null ? dateErrorId : blocked === null ? undefined : reasonId
                  }
                  onClick={() => {
                    if (blocked === null) onSave();
                  }}
                >
                  {saving
                    ? t('web:booking.trainer.exception.saving')
                    : t('web:booking.trainer.exception.save')}
                </button>
              </div>
            </>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
